import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class ClientAdminService {
  private readonly adminApiUrl = environment.admin_api_url;
  private readonly baseUrl = environment.base_url;

  private static readonly FORM_HEADERS = new HttpHeaders({
    'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8',
  });

  constructor(private http: HttpClient) {}

  /**
   * Serialises a flat object into x-www-form-urlencoded string,
   * matching the legacy AngularJS $httpProvider.defaults.transformRequest.
   */
  private toFormBody(obj: Record<string, any>): string {
    const parts: string[] = [];
    const encode = (key: string, value: any): void => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach((v, i) => encode(`${key}[${i}]`, v));
      } else if (typeof value === 'object' && !(value instanceof File)) {
        Object.keys(value).forEach(sub => encode(`${key}[${sub}]`, value[sub]));
      } else {
        parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(String(value)));
      }
    };
    Object.keys(obj).forEach(k => encode(k, obj[k]));
    return parts.join('&');
  }

  private postForm(url: string, data: Record<string, any>): Observable<any> {
    return this.http.post<any>(url, this.toFormBody(data), {
      headers: ClientAdminService.FORM_HEADERS,
      withCredentials: true,
    });
  }

  getAllClients(): Observable<any> {
    return this.postForm(`${this.adminApiUrl}/getAllClients`, {});
  }

  saveClient(client: any): Observable<any> {
    const payload = { ...client };
    delete payload.flow;
    return this.postForm(`${this.adminApiUrl}/saveClient`, {
      data: JSON.stringify(payload),
    });
  }

  deleteClient(clientKey: string): Observable<any> {
    return this.postForm(`${this.baseUrl}/deleteClient`, { data: clientKey });
  }

  toggleClientActiveStatus(clientKey: string, isActive: string): Observable<any> {
    return this.postForm(`${this.adminApiUrl}/toggleClientActiveStatus`, {
      clientKey,
      clientActive: isActive,
    });
  }

  push(): Observable<any> {
    return this.postForm(`${this.adminApiUrl}/rds/takeSnapshotOfStarterClient`, {});
  }

  downloadClientLogo(clientKey: string): Observable<Blob> {
    return this.http.get(`${this.adminApiUrl}/downloadClientLogo/${clientKey}`, {
      responseType: 'blob',
      withCredentials: true,
    });
  }

  getEnvironmentType(): Observable<any> {
    return this.http.get<any>(`${this.adminApiUrl}/getEnvType`, { withCredentials: true });
  }

  checkClientName(clientName: string): Observable<any> {
    return this.postForm(`${this.adminApiUrl}/checkClientName`, {
      client_name: clientName,
    });
  }

  getClientSetupData(data: any): Observable<any> {
    return this.postForm(`${this.baseUrl}/getNavItemsByClient`, data);
  }

  getClientLandingDetails(clientKey: string): Observable<any> {
    return this.postForm(`${this.adminApiUrl}/getClientLandDtlsByKey`, {
      client_key: clientKey,
    });
  }

  saveClientLandingDetails(payload: any): Observable<any> {
    return this.postForm(`${this.adminApiUrl}/saveClientLandingDtls`, {
      data: JSON.stringify(payload),
    });
  }

  getWorkflowTypes(clientKey: string): Observable<any> {
    return this.http.get<any>(
      `${this.adminApiUrl}/getWorkflowTypes`,
      { params: { clientKey }, withCredentials: true },
    );
  }

  getTaxYearAndScenarioFilters(clientKey: string): Observable<any> {
    return this.http.get<any>(
      `${this.adminApiUrl}/getTaxyearAndScenarioFilters`,
      { params: { clientKey }, withCredentials: true },
    );
  }

  getWorkflowTree(wfstKey: string, taxYear: string, scenario: string, clientKey: string): Observable<any> {
    return this.postForm(`${this.adminApiUrl}/getWorkflowTree`, {
      wfstKey, taxYear, scenario, clientKey,
    });
  }

  getOnlyDefaultWorkflowTree(wfstKey: string, taxYear: string, scenario: string, clientKey: string): Observable<any> {
    return this.postForm(`${this.adminApiUrl}/getOnlyDefaultWorkflowTree`, {
      wfstKey, taxYear, scenario, clientKey,
    });
  }

  saveClientWorkflow(wfstKey: string, taxYear: string, scenario: string, clientKey: string, items: any[]): Observable<any> {
    return this.postForm(`${this.adminApiUrl}/saveClientWorkflow`, {
      wfstKey, taxYear, scenario, clientKey,
      clientWorkflowItems: JSON.stringify(items),
    });
  }

  addWorkflow(params: Record<string, any>): Observable<any> {
    return this.postForm(`${this.adminApiUrl}/addWorkflow`, params);
  }

  editWorkflow(params: Record<string, any>): Observable<any> {
    return this.postForm(`${this.adminApiUrl}/editWorkflow`, params);
  }

  getWorkflowComponent(pType: string, clientKey: string, wfscompKey: string, taxYear: string, scenario: string): Observable<any> {
    return this.postForm(`${this.adminApiUrl}/getWorkflowComponent`, {
      p_type: pType, client_key: clientKey, wfscomp_key: wfscompKey, tax_year: taxYear, scenario: scenario,
    });
  }

  getAllVersionNumbers(pType: string, clientKey: string, wfscompKey: string, taxYear: string, scenario: string): Observable<any> {
    return this.postForm(`${this.adminApiUrl}/getAllVersionNumbers`, {
      p_type: pType, client_key: clientKey, wfscomp_key: wfscompKey, tax_year: taxYear, scenario: scenario,
    });
  }

  saveAppSetup(clientKey: string, navItems: any[]): Observable<any> {
    return this.http.post<any>(
      `${this.adminApiUrl}/saveAppSetup?clientKey=${clientKey}`,
      JSON.stringify(navItems),
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }), withCredentials: true },
    );
  }

  uploadClientLogo(clientKey: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(
      `${this.adminApiUrl}/uploadClientLogo?clientKey=${clientKey}`,
      formData,
      { withCredentials: true },
    );
  }

  removeClientLogo(clientKey: string, fileName: string): Observable<any> {
    return this.postForm(`${this.adminApiUrl}/removeClientLogo`, {
      data: clientKey,
      flowFilename: fileName,
    });
  }
}
